import * as React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { cn, FileType } from '@restworld/utils-common';

import { Heading } from '@restworld/ui-ds-base';
import { CardUploadIcon } from './svg';

interface IUploadCardProps {
  fileType?: string | string[];
  setUploadFiles: (value: FileType) => void;
  isError?: boolean;
  testId?: string;
}
export const UploadCard: React.FunctionComponent<IUploadCardProps> = ({
  fileType = ['.pdf'],
  setUploadFiles,
  isError,
  testId,
}) => {
  const acceptedFormats = useMemo(
    () =>
      typeof fileType === 'string' ? fileType : Array.isArray(fileType) ? fileType?.join(',') : AcceptedFileType.Pdf,
    [fileType]
  );
  const { t } = useTranslation();
  return (
    <Wrapper data-cy={testId}>
      <div className="flex items-center justify-center w-full">
        <LabelStyled isError={isError} htmlFor="dropzone-file" data-cy="dropzone-file">
          <div className="flex flex-col items-center justify-center py-6 px-11 gap-2">
            <CardUploadIcon stroke={isError ? '#ba001f' : '#8e69ce'} />
            <p className="mb-2 text-sm text-black">
              <UnderLinedText isError={isError}>{t('common:click_here')}</UnderLinedText>{' '}
              {t('common:to_upload_your_file')}
            </p>
            <Heading
              as="span"
              size={6}
              weight="normal"
              color="muted"
              className="text-xs font-light"
              data-cy="upload-a-file-text"
            >
              {`${t('common:upload_a_file')}: ${acceptedFormats.replace(/,/g, ', ')} (20MB max)`}
            </Heading>
          </div>
          <Input
            type="file"
            id="dropzone-file"
            hidden
            onChange={(event) => setUploadFiles(event.target.files)}
            accept={acceptedFormats}
            data-cy="upload-a-file-input"
          />
        </LabelStyled>
      </div>
    </Wrapper>
  );
};

export default UploadCard;
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('w-full', className)} {...props} />
);
const Input = ({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input className={cn('', className)} {...props} />
);
const AcceptedFileType = {
  Text: '.txt',
  Gif: '.gif',
  Jpeg: '.jpg',
  Png: '.png',
  Doc: '.doc',
  Pdf: '.pdf',
  AllImages: 'image/*',
  AllVideos: 'video/*',
  AllAudios: 'audio/*',
};
const LabelStyled = ({ isError, className, ...props }: any) => (
  <label
    className={cn(
      'flex flex-col items-center justify-center w-full h-full border-2 border-primary-800 border-dashed rounded-[1rem] cursor-pointer bg-primary-50',
      isError ? 'border-red-600 bg-red-50' : '',
      className
    )}
    {...props}
  />
);
const UnderLinedText = ({ isError, className, ...props }: any) => (
  <span
    className={cn(
      'font-semibold text-primary-800 underline underline-offset-4',
      isError ? 'text-red-600' : 'text-primary-800',
      className
    )}
    {...props}
  />
);
